import {CdkDragDrop, moveItemInArray} from '@angular/cdk/drag-drop';
import {Component, OnInit} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {MatSnackBar} from '@angular/material/snack-bar';
import {forkJoin} from 'rxjs';
import {CollectionService} from 'src/app/services/collection.service';
import {StorageKey, StorageService} from 'src/app/services/common/storage.service';
import {ProductService} from 'src/app/services/product.service';
import {UtilityService} from 'src/app/services/utility.service';

interface CollectionRow {
  id?: string;
  _id?: string;
  name: string;
  itemsCount?: number;
  visible: boolean;
  order: number;
  bannerUrl?: string;
  showInCatalog?: boolean;
  sortOrder?: number;
  updatedAt?: string;
  createdAt?: string;
}

@Component({
  selector: 'app-collection',
  templateUrl: './collection.component.html',
  styleUrls: ['./collection.component.scss'],
})
export class CollectionComponent implements OnInit {
  collections: CollectionRow[] = [];
  filteredCollections: CollectionRow[] = [];
  selectedCollection: CollectionRow | null = null;
  listSearch = '';
  isNew = true;
  saving = false;
  deleting = false;
  submitDisabled = false;
  lastSavedCollectionId: string | null = null;

  collectionForm: FormGroup = new FormGroup({
    name: new FormControl('', [Validators.required, Validators.maxLength(100)]),
    showInCatalog: new FormControl(true),
    sortOrder: new FormControl(1, [Validators.required, Validators.min(1)]),
  });

  bannerPreviewUrl: string | null = null;
  bannerFile: File | null = null;
  readonly bannerMaxBytes = 5 * 1024 * 1024;
  readonly allowedBannerTypes = ['image/png', 'image/jpeg', 'image/webp'];

  allProducts: any[] = [];
  availableProducts: any[] = [];
  collectionProducts: any[] = [];

  collectionProductIds: string[] = [];
  collectionVariantIds: string[] = [];

  constructor(
    private collectionService: CollectionService,
    private storageService: StorageService,
    private productService: ProductService,
    private utilityService: UtilityService,
    private snack: MatSnackBar,
  ) {}

  ngOnInit(): void {
    this.loadCollections();
    this.loadAvailableItems();
    this.createNewCollection();
    this.collectionForm.valueChanges.subscribe(() => {
      this.submitDisabled = false;
    });
  }

  get nameCharCount(): number {
    return (this.collectionForm.get('name')?.value || '').length;
  }

  getCollectionId(row: CollectionRow | null): string {
    return String(row?._id ?? row?.id ?? '');
  }

  normalizeCollection(row: any, index = 0): CollectionRow {
    const sortOrder = Number(row?.sortOrder ?? row?.order ?? index + 1);
    const showInCatalog = row?.showInCatalog ?? row?.visible ?? true;
    return {
      ...row,
      visible: !!showInCatalog,
      order: sortOrder,
      showInCatalog: !!showInCatalog,
      sortOrder,
      bannerUrl: row?.bannerUrl || row?.photo || '',
    };
  }

  loadCollections(afterLoad?: () => void): void {
    this.collectionService.list().subscribe((response: any) => {
      if (response.success) {
        const loaded: CollectionRow[] = (response.data || []).map((row: any, index: number) =>
          this.normalizeCollection(row, index),
        );

        loaded.sort((a: any, b: any) => {
          if (this.lastSavedCollectionId) {
            const idA = this.getCollectionId(a);
            const idB = this.getCollectionId(b);
            if (idA === this.lastSavedCollectionId) return -1;
            if (idB === this.lastSavedCollectionId) return 1;
          }
          const timeB = new Date(b.updatedAt || b.createdAt || 0).getTime();
          const timeA = new Date(a.updatedAt || a.createdAt || 0).getTime();
          if (timeB && timeA && timeB !== timeA) {
            return timeB - timeA;
          }
          const idA = String(a._id || a.id || '');
          const idB = String(b._id || b.id || '');
          if (idB && idA && idB.length === 24 && idA.length === 24) {
            return idB.localeCompare(idA);
          }
          return 0;
        });

        this.collections = loaded;
        this.onListSearch();
        if (afterLoad) afterLoad();
      }
    });
  }

  onListSearch(): void {
    const q = this.listSearch.trim().toLowerCase();
    this.filteredCollections = !q
      ? [...this.collections]
      : this.collections.filter(c => c.name?.toLowerCase().includes(q));
  }

  resetListFilters(): void {
    this.listSearch = '';
    this.onListSearch();
  }

  selectCollection(row: CollectionRow): void {
    const collection = this.normalizeCollection(row);
    this.selectedCollection = collection;
    this.isNew = false;
    this.collectionForm.setValue({
      name: collection.name || '',
      showInCatalog: collection.visible,
      sortOrder: collection.order,
    }, {emitEvent: false});
    this.bannerPreviewUrl = collection.bannerUrl || null;
    this.bannerFile = null;
    this.submitDisabled = true;
    this.loadCollectionItems(this.getCollectionId(collection));
  }

  createNewCollection(): void {
    this.selectedCollection = null;
    this.isNew = true;
    this.collectionForm.reset({
      name: '',
      showInCatalog: true,
      sortOrder: 1,
    }, {emitEvent: false});
    this.bannerPreviewUrl = null;
    this.bannerFile = null;
    this.collectionProductIds = [];
    this.collectionVariantIds = [];
    this.collectionProducts = [];
    this.prepareCollectionProductLists();
    this.submitDisabled = false;
  }

  onBannerSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    if (!this.allowedBannerTypes.includes(file.type)) {
      input.value = '';
      return;
    }
    if (file.size > this.bannerMaxBytes) {
      input.value = '';
      return;
    }

    this.bannerFile = file;
    this.submitDisabled = false;
    const reader = new FileReader();
    reader.onload = () => (this.bannerPreviewUrl = reader.result as string);
    reader.readAsDataURL(file);
    input.value = '';
  }

  resetCrop(): void {
    this.bannerFile = null;
    this.bannerPreviewUrl = this.selectedCollection?.bannerUrl || null;
  }

  autoCropAndResize(): void {
    if (!this.bannerFile && !this.bannerPreviewUrl) {
      return;
    }

    const targetW = 1920;
    const targetH = 640;

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = targetW;
      canvas.height = targetH;
      const ctx = canvas.getContext('2d')!;

      const srcRatio = img.width / img.height;
      const targetRatio = targetW / targetH;

      let sx = 0;
      let sy = 0;
      let sw = img.width;
      let sh = img.height;

      if (srcRatio > targetRatio) {
        sw = img.height * targetRatio;
        sx = (img.width - sw) / 2;
      } else {
        sh = img.width / targetRatio;
        sy = (img.height - sh) / 2;
      }

      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, targetW, targetH);

      canvas.toBlob(blob => {
        if (!blob) return;
        const fileName = this.bannerFile?.name || 'collection-banner.jpg';
        this.bannerFile = new File([blob], fileName, {type: 'image/jpeg'});
        this.bannerPreviewUrl = canvas.toDataURL('image/jpeg', 0.92);
        this.submitDisabled = false;
       this.snack.open('Banner cropped & resized to 4:1 (2000x500 px)', 'OK', {duration: 2000}); 
      }, 'image/jpeg', 0.92);
    };

    img.src = this.bannerPreviewUrl!;
  }

  submit(): void {
    if (this.collectionForm.invalid) {
      this.collectionForm.markAllAsTouched();
      return;
    }

    const body = new FormData();
    body.append('name', this.collectionForm.value.name);
    body.append('showInCatalog', String(this.collectionForm.value.showInCatalog));
    body.append('sortOrder', String(this.collectionForm.value.sortOrder));
    if (!this.bannerFile && this.bannerPreviewUrl) body.append('photo', this.bannerPreviewUrl);
    if (this.bannerFile) body.append('photo', this.bannerFile);

    this.saving = true;
    const isCreating = this.isNew;
    const request$ = isCreating
      ? this.collectionService.create(body)
      : this.collectionService.update(this.getCollectionId(this.selectedCollection), body);

    request$.subscribe({
      next: (response: any) => {
        this.saving = false;
        if (!response.success) {
          return;
        }

        const savedCollectionId = this.getCollectionId(response.data) || this.getCollectionId(this.selectedCollection);
        this.lastSavedCollectionId = savedCollectionId;

        if (!isCreating && savedCollectionId && this.collectionProductIds.length > 0) {
          this.saveCollectionItems(savedCollectionId, false);
        }

        this.loadCollections(() => this.createNewCollection());
      },
      error: () => {
        this.saving = false;
      },
    });
  }

  deleteCollection(row?: CollectionRow): void {
    const target = row || this.selectedCollection;
    const id = this.getCollectionId(target);
    if (!id || this.deleting) return;
    if (!confirm('Delete this collection?')) return;

    this.deleting = true;
    this.collectionService.delete(id).subscribe({
      next: (response: any) => {
        this.deleting = false;
        if (response.success) {
          if (this.lastSavedCollectionId === id) {
            this.lastSavedCollectionId = null;
          }
          this.createNewCollection();
          this.loadCollections();
        }
      },
      error: () => {
        this.deleting = false;
      },
    });
  }

  loadAvailableItems(): void {
    const currentUserData = JSON.parse(this.storageService.getValue(StorageKey.currentUser));
    this.utilityService.getProductDetails(currentUserData.id).subscribe((response: any) => {
      this.allProducts = (response.data || [])
        .filter((product: any) => product.isActive !== false)
        .map((product: any) => ({
          ...product,
          productId: String(product.productId),
          variantIds: (product.variantIds || []).map((variantId: any) => String(variantId)),
        }));
      this.prepareCollectionProductLists();
    });
  }

  expandProduct = (productId: string): void => {
    this.productService.productVariantList({
      pageSize: 99999,
      pageIndex: 1,
      productId,
    }).subscribe((response: any) => {
      if (!response.success) return;
      const variants = (response.data || [])
        .filter((variant: any) => variant.isActive !== false)
        .map((variant: any) => ({
          variantId: String(variant.variantId),
          name: Object.values(variant.attributeValues || {}).join('-'),
          productId: String(variant.productId),
        }));

      const availableVariants = variants.filter((variant: any) =>
        !this.collectionVariantIds.includes(variant.variantId),
      );
      const selectedVariants = variants.filter((variant: any) =>
        this.collectionVariantIds.includes(variant.variantId),
      );

      this.availableProducts = this.availableProducts.map((product: any) =>
        product.productId === productId ? {...product, variants: availableVariants} : product,
      );
      this.collectionProducts = this.collectionProducts.map((product: any) =>
        product.productId === productId ? {...product, variants: selectedVariants} : product,
      );
    });
  }

  loadCollectionItems(collectionId: string): void {
    if (!collectionId) return;
    this.collectionService.items(collectionId).subscribe((response: any) => {
      if (!response.success) return;
      const collectionItems = response.data || [];
      const productIds = collectionItems.map((item: any) => String(item.productId));
      const variantIds = collectionItems.reduce((ids: string[], item: any) => {
        if (Array.isArray(item.variantIds)) {
          ids.push(...item.variantIds.map((variantId: any) => String(variantId)));
        } else if (item.variantId) {
          ids.push(String(item.variantId));
        }
        return ids;
      }, []);
      this.collectionProductIds = Array.from(new Set(productIds));
      this.collectionVariantIds = Array.from(new Set(variantIds));
      this.prepareCollectionProductLists();
    });
  }

  moveCollectionProduct = (type: 'add' | 'subtract') => (productId: string, variantId?: string): void => {
    if (type === 'add') {
      this.collectionProductIds = Array.from(new Set([...this.collectionProductIds, productId]));
      if (variantId) {
        this.collectionVariantIds = Array.from(new Set([...this.collectionVariantIds, variantId]));
      } else {
        const matchedProductVariants: string[] = this.getProductVariantIds(productId);
        this.collectionVariantIds = Array.from(new Set([...this.collectionVariantIds, ...matchedProductVariants]));
      }
    } else if (variantId) {
      this.collectionVariantIds = this.collectionVariantIds.filter(id => id !== variantId);
      const matchedProductVariants: string[] = this.getProductVariantIds(productId);
      if (matchedProductVariants.every(id => !this.collectionVariantIds.includes(id))) {
        this.collectionProductIds = this.collectionProductIds.filter(id => id !== productId);
      }
    } else {
      this.collectionProductIds = this.collectionProductIds.filter(id => id !== productId);
      const matchedProductVariants: string[] = this.getProductVariantIds(productId);
      this.collectionVariantIds = this.collectionVariantIds.filter(id => !matchedProductVariants.includes(id));
    }

    this.prepareCollectionProductLists(productId);
  }

  prepareCollectionProductLists(expandedProductId?: string): void {
    this.availableProducts = this.allProducts.filter((product: any) => {
      if (!this.collectionProductIds.includes(product.productId)) return true;
      if (product.type === 'Product Group') {
        const variantIds = product.variantIds || [];
        if (!variantIds.length) return false;
        const availableVariantIds = variantIds.filter((variantId: string) =>
          !this.collectionVariantIds.includes(variantId),
        );
        return availableVariantIds.length > 0 && availableVariantIds.length < variantIds.length;
      }
      return false;
    });
    this.collectionProducts = this.allProducts.filter((product: any) =>
      this.collectionProductIds.includes(product.productId),
    );

    if (expandedProductId) this.expandProduct(expandedProductId);
  }

  getProductVariantIds(productId: string): string[] {
    return this.allProducts.find(product => product.productId === productId)?.variantIds || [];
  }

  saveCollectionItems(collectionId = this.getCollectionId(this.selectedCollection), showMessage = true): void {
    if (!collectionId) return;

    const payload = this.allProducts.reduce((acc: {productId: string, variantIds: string[]}[], product: any) => {
      if (this.collectionProductIds.includes(product.productId)) {
        acc.push({
          productId: product.productId,
          variantIds: (product.variantIds || []).filter((variantId: string) =>
            this.collectionVariantIds.includes(variantId),
          ),
        });
      }
      return acc;
    }, []);

    this.collectionService.saveItems(collectionId, payload).subscribe((response: any) => {
      if (response.success) {
        this.loadCollections();
      }
    });
  }

  onRowDrop(event: CdkDragDrop<CollectionRow[]>): void {
    if (event.previousIndex === event.currentIndex) {
      return;
    }

    moveItemInArray(this.filteredCollections, event.previousIndex, event.currentIndex);

    this.filteredCollections.forEach((row, index) => {
      row.order = index + 1;
      row.sortOrder = row.order;
    });

    this.filteredCollections.forEach(row => {
      const master = this.collections.find(c => this.getCollectionId(c) === this.getCollectionId(row));
      if (master) {
        master.order = row.order;
        master.sortOrder = row.sortOrder;
      }
    });

    const movedRow = this.filteredCollections[event.currentIndex];
    this.saveCollectionOrder(movedRow);
  }

  private saveCollectionOrder(target: CollectionRow | CollectionRow[]): void {
    const row = Array.isArray(target) ? target[0] : target;
    const id = this.getCollectionId(row);
    if (!id) return;

    const body = new FormData();
    body.append('name', row.name || '');
    body.append('showInCatalog', String(row.showInCatalog ?? row.visible));
    body.append('sortOrder', String(row.sortOrder ?? row.order));
    if (row.bannerUrl) body.append('photo', row.bannerUrl);

    this.collectionService.update(id, body).subscribe({
      next: () => {},
      error: () => {},
    });
  }
}